import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from '@mui/material';
import DialogSearch from '@/components/DialogSearch/DialogSearch';
import { formatSummaryUser, userSearchToUserInfo } from '@/helpers/utils';
import useToastContext from '@/hooks/useToastContext';
import { getPlantsForm } from '@/redux/Forms/forms.reducer';
import usersServices from '@/services/usersServices';
import { Plant, UserInfo, UserSearch } from '@/types';
import { IcoButtonSimple } from '../IcoButton/IcoButton';
import InputUser from '../InputUser/InputUser';
import MandatoryLabel from '../MandatoryLabel/MandatoryLabel';
import SimpleButton from '../SimpleButton/SimpleButton';
import TableListEditableNew from '../TableListEditable/TableListEditableNew';
import styles from './UsersSelection.module.scss';

const formatWorkplace = (wkzPlant: string, plants: Plant[]) => {
  const plant = plants.find((p) => `${p.wkzNumber}` === `${wkzPlant}`);

  return plant ? `${plant?.plantName} (${wkzPlant})` : wkzPlant;
};

interface UsersSelectionProps {
  id: string;
  value: string[] | string | UserInfo;
  displayInTable: boolean;
  popupTitle?: string;
  onChange: (users: string[] | string | null | UserInfo) => void;
  isMandatory?: boolean;
  plants: Plant[];
  onlyOneUser?: boolean;
  tableTitle?: string;
  buttonLabel?: string;
  isOnlyOneUserSelection?: boolean;
  onlyOneUserIsObject?: boolean;
}

const USER_COLUMN = {
  WORKPLACE: 'plant1',
  LAST_NAME: 'lastName',
  FIRST_NAME: 'firstName',
  DEPARTMENT: 'department',
  USER_CODE: 'userCode',
} as const;

const UsersSelection: React.FC<UsersSelectionProps> = (props) => {
  const { id, value = [], displayInTable = false, popupTitle = '', onChange, isMandatory = false, plants, onlyOneUser = false, tableTitle = '',
    buttonLabel = '', isOnlyOneUserSelection = false, onlyOneUserIsObject = false } = props;
  const normalizedValue = useMemo<string[]>(() => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value.length > 0) return [value];

    return [];
  }, [value]);
  const [popupOpen, setPopupOpen] = useState(false);
  const [userIds, setUserIds] = useState<string[]>(normalizedValue);
  const [userSelected, setUserSelected] = useState<UserSearch | null>(null);
  const [usersData, setUsersData] = useState<UserSearch[]>([]);
  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState<string[]>([]);
  const [resetFilters, setResetFilters] = useState(0);
  const { addToast } = useToastContext();
  const { t } = useTranslation();

  const getSelectedUsersData = useCallback(
    () => usersData.filter((user) => selection.includes(user[USER_COLUMN.USER_CODE])),
    [selection, usersData],
  );

  const getUsersAfterPendingChanges = useCallback(() => {
    const baseUsers = userIds.filter((user) => user !== '');

    if (selection.length === 0) return baseUsers;

    const selectionData = getSelectedUsersData();

    return baseUsers.filter((userCode) => !selectionData.find((user) => user.userCode === userCode));
  }, [userIds, selection, getSelectedUsersData]);

  const hasPendingChanges = useMemo(() => {
    const initialUsers = [...normalizedValue.filter((user) => user !== '')].sort();
    const nextUsers = [...getUsersAfterPendingChanges()].sort();

    if (initialUsers.length !== nextUsers.length) return true;

    return initialUsers.some((user, index) => user !== nextUsers[index]);
  }, [normalizedValue, getUsersAfterPendingChanges]);

  const handleOnSave = () => {
    onChange(getUsersAfterPendingChanges());

    setPopupOpen(false);
  };

  const handleOnAddUser = () => {
    let users = userIds.concat(userSelected?.userCode ?? '');
    users = [...new Set(users)]; // remove possible duplicates
    setUserIds(users);
    setUserSelected(null);
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
    setUserSelected(null);
    setUserIds(normalizedValue);
  };

  const handleDeleteUsers = () => {
    const selectionData = getSelectedUsersData();
    setUserIds(userIds.filter((userCode) => !selectionData.find((user) => user.userCode === userCode)));
  };

  const handleSelectOnlyOneUser = (user: UserSearch | null) => {
    onChange(onlyOneUserIsObject ? userSearchToUserInfo(user) : user?.userCode ?? '');
    setPopupOpen(false);
  };

  useEffect(() => {
    if (popupOpen && !isOnlyOneUserSelection) {
      setSelection([]);

      if (userIds.filter((u) => u !== '').length > 0) {
        setLoading(true);
        usersServices.getUsersByList(userIds)
          .then((uData) => {
            const userWithPlants = uData?.usersResponseFindAll
              ?.map((user: UserSearch) => ({ ...user, plant1: formatWorkplace(user.plant1, plants) })) ?? [];
            setUsersData(userWithPlants);
          })
          .catch((error) => addToast(error.message, 'ERROR'))
          .finally(() => setLoading(false));
      } else {
        setUsersData([]);
      }
    }
  }, [addToast, isOnlyOneUserSelection, plants, popupOpen, userIds]);

  useEffect(() => {
    setUserIds(normalizedValue);
  }, [normalizedValue]);

  const usersColumns = [
    { name: USER_COLUMN.WORKPLACE, title: t('dialog.workplace'), flex: 2 },
    { name: USER_COLUMN.LAST_NAME, title: t('dialog.lastName') },
    { name: USER_COLUMN.FIRST_NAME, title: t('dialog.firstName') },
    { name: USER_COLUMN.DEPARTMENT, title: t('dialog.department') },
    { name: USER_COLUMN.USER_CODE, title: t('dialog.userId') },
  ];

  const tableUsersData = useMemo(
    () => usersData.map((user) => ({ ...user } as { onClick?: () => void } & Record<string, unknown>)),
    [usersData],
  );

  const summaryValue = displayInTable ? value : userIds;
  const summaryText = Array.isArray(summaryValue)
    ? summaryValue.join(' | ')
    : typeof summaryValue === 'string'
      ? summaryValue
      : '';

  return (
    <>
      <div className={styles.UsersSummary} onClick={!displayInTable ? () => setPopupOpen(true) : () => null}>
        <div>
          <IconButton id={id} data-testid={id} onClick={() => setPopupOpen(true)} size="large">
            <i className={`di icon-person-gruppe ${styles.Icon}`} />
            {isMandatory && <MandatoryLabel className={`${styles.MandatoryIcon} error`} />}
          </IconButton>
        </div>
        <div>
          {(isOnlyOneUserSelection && onlyOneUserIsObject)
            ? (value) ? (value as unknown as UserInfo)?.summary : ''
            : summaryText}
        </div>
      </div>
      {(popupOpen && !isOnlyOneUserSelection) && (
        <Dialog id={id} data-testid={id} open={popupOpen} maxWidth={false}>
          <DialogTitle>{popupTitle || t('dialog.giveAuth')}</DialogTitle>
          <DialogContent className={styles.DialogContent}>
            <>
              <div className={styles.AddUserSection}>
                <div className={styles.InputUserContainer}>
                  <InputUser
                    id="input-user-selection-dialog"
                    title={t('dialog.searchUser')}
                    label={t('dialog.searchUser')}
                    value={userSelected ? formatSummaryUser(userSelected) : ''}
                    code={userSelected?.userCode ?? ''}
                    clear={() => setUserSelected(null)}
                    updateUserSelected={(user) => setUserSelected(user)}
                    disabled={onlyOneUser && usersData.length >= 1}
                  />
                </div>
                <SimpleButton
                  handleClick={handleOnAddUser}
                  id="btn-add-user"
                  color="primary"
                  buttonText={buttonLabel || t('dialog.giveAuth')}
                  textStyle={styles.GiveAuthButton}
                  disabled={!userSelected}
                />
              </div>
              <Divider />
            </>
            <div>
              <div className={styles.TableUsersHeader}>
                <DialogTitle className={styles.TableUsersTitle}>{tableTitle || t('dialog.existingAuth')}</DialogTitle>
                <div className={styles.ButtonsContentTableRight}>
                  <SimpleButton
                    handleClick={() => setResetFilters(resetFilters + 1)}
                    id="btn-reset-filters"
                    color="primary"
                    buttonText={t('dialog.resetFilter')}
                    textStyle={styles.GiveAuthButton}
                  />
                  <IcoButtonSimple
                    disabled={!(selection.length > 0)}
                    id="btn-delete-users"
                    icon="icon-muelleimer-loeschen"
                    tooltipText={t('software.delete')}
                    handleClick={handleDeleteUsers}
                  />
                </div>
              </div>
              <TableListEditableNew
                isFiltering
                hasCheckbox
                list={tableUsersData}
                columns={usersColumns}
                columnId={USER_COLUMN.USER_CODE}
                selection={selection}
                onSelectionChange={(selected) => setSelection(selected as string[])}
                loading={loading}
                resetFilters={resetFilters}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" color="primary" onClick={handleOnSave} disabled={!hasPendingChanges} data-testid="save-userselection-popup">
              {t('dialog.save')}
            </Button>
            <Button color="primary" onClick={handleClosePopup} data-testid="cancel-userselection-popup">{t('dialog.cancel')}</Button>
          </DialogActions>
        </Dialog>
      )}

      {(popupOpen && isOnlyOneUserSelection) && (
        <DialogSearch
          isRemote
          defaultValue={
            (isOnlyOneUserSelection)
              ? (value) ? (onlyOneUserIsObject ? (value as unknown as UserInfo)?.code : (value as unknown as string)) : ''
              : (Array.isArray(userIds)) ? userIds[0] : (userIds as string)
          }
          title={popupTitle}
          close={handleClosePopup}
          emptyUserAllowed
          updateOptionSelected={(user: UserSearch | UserSearch[] | null) => handleSelectOnlyOneUser(Array.isArray(user) ? user[0] ?? null : user)}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  plants: getPlantsForm(state),
});

export default connect(mapStateToProps)(UsersSelection);
